import React, { useState, useEffect } from 'react';

const Login = ({ onLogin }) => {
  const [facilities, setFacilities] = useState([]);
  const [selectedFacility, setSelectedFacility] = useState('');
  const [accessToken, setAccessToken] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchFacilities = async () => {
      try {
        const response = await fetch('/api/facilities');
        if (!response.ok) {
          throw new Error('Failed to fetch facilities');
        }
        const data = await response.json();
        setFacilities(data);
      } catch (err) {
        setError('Error loading facilities: ' + err.message);
      }
    };

    fetchFacilities();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await fetch('/api/verify-access', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          providerId: selectedFacility,
          accessToken: accessToken,
        }),
      });

      if (!response.ok) {
        throw new Error('Invalid access token');
      }

      const userData = await response.json();
      const selectedFacilityData = facilities.find(f => f.provider_id === selectedFacility);
      
      onLogin(userData, selectedFacilityData);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
    <div className="login-page">
      <div className="login-container">
        <div className="card">
          <div className="card-header">
            <h1 className="text-type-1-bold text-header">Welcome to JJ Bed Providers Tool</h1>
            <p className="text-type-5 text-body">
              Please select your facility and enter your access token to continue.
            </p>
          </div>

          {error && (
            <div className="alert alert-error" role="alert">
              <strong>Authentication Error:&nbsp;</strong>{error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="login-form">
            <div className="form-group">
              <label htmlFor="facility" className="form-label">
                Select Facility <span className="text-danger">*</span>
              </label>
              <select
                id="facility"
                value={selectedFacility}
                onChange={(e) => setSelectedFacility(e.target.value)}
                required
                className="form-control"
                aria-describedby="facility-help"
                style={{ fontSize: '16px' }}
              >
                <option value="">Please select a facility</option>
                {facilities.map((facility) => (
                  <option key={facility.provider_id} value={facility.provider_id}>
                    {facility.provider_name}
                  </option>
                ))}
              </select>
              <small id="facility-help" className="form-help text-muted">
                Choose your facility from the dropdown list
              </small>
            </div>

            <div className="form-group">
              <label htmlFor="accessToken" className="form-label">
                Access Token <span className="text-danger">*</span>
              </label>
              <div className="password-input-wrapper">
                <input
                  type={showPassword ? "text" : "password"}
                  id="accessToken"
                  value={accessToken}
                  onChange={(e) => setAccessToken(e.target.value)}
                  required
                  className="form-control password-input"
                  aria-describedby="token-help"
                  placeholder="Enter your access token"
                  style={{ fontSize: '16px' }}
                />
                <button
                  type="button"
                  onClick={togglePasswordVisibility}
                  className="password-toggle-btn"
                  aria-label={showPassword ? "Hide access token" : "Show access token"}
                  aria-pressed={showPassword}
                  tabIndex="0"
                >
                  <span className="password-toggle-icon" aria-hidden="true">
                    {showPassword ? "Hide" : "Show"}
                  </span>
                </button>
              </div>
              <small id="token-help" className="form-help text-muted">
                Enter the access token provided in the weekly email
              </small>
            </div>

            <div className="form-actions">
              <button
                type="submit"
                disabled={loading || !selectedFacility || !accessToken}
                className="btn btn-primary"
              >
                {loading ? 'Authenticating...' : 'Login'}
              </button>
            </div>
          </form>

          <div className="contact-info">
            <p className="text-type-5 text-muted">
              For technical assistance or questions, please contact{' '}
              <a 
                href="mailto:MDHHS-JJReform@michigan.gov" 
                className="contact-email"
                aria-label="Contact MDHHS JJ Reform via email"
              >
                MDHHS-JJReform@michigan.gov
              </a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;